/**
 * Pre-emphasis Filter
 * Priority 4: 10-20% quality improvement
 *
 * High-frequency boost to enhance consonant clarity.
 * Particularly effective in noisy environments.
 */

import { IAudioProcessor, PreEmphasisConfig } from '../core/types'

/**
 * Pre-emphasis Filter
 *
 * A first-order high-pass filter that boosts high frequencies:
 * y[n] = x[n] - coefficient * x[n-1]
 *
 * Benefits:
 * - Enhances consonants (s, t, k, p sounds)
 * - Improves speech intelligibility
 * - Better recognition in noisy environments
 * - Balances spectral energy for ASR engines
 *
 * Common in speech processing before ASR analysis.
 */
export class PreEmphasisFilter implements IAudioProcessor {
  private config: PreEmphasisConfig
  private lastSample: number = 0

  constructor(config: Partial<PreEmphasisConfig> = {}) {
    this.config = {
      coefficient: config.coefficient ?? 0.97  // Standard speech pre-emphasis
    }
  }

  getName(): string {
    return 'PreEmphasis'
  }

  /**
   * Apply pre-emphasis filter to audio buffer
   * @param buffer Input audio samples
   * @returns High-frequency enhanced output
   */
  process(buffer: Float32Array): Float32Array {
    const output = new Float32Array(buffer.length)

    // First sample uses last sample from previous buffer for continuity
    output[0] = buffer[0] - this.config.coefficient * this.lastSample

    // Process remaining samples
    for (let i = 1; i < buffer.length; i++) {
      output[i] = buffer[i] - this.config.coefficient * buffer[i - 1]
    }

    // Store last sample for next buffer
    this.lastSample = buffer[buffer.length - 1]

    return output
  }

  /**
   * Reset filter state
   */
  reset(): void {
    this.lastSample = 0
  }

  /**
   * Get current coefficient (for monitoring/debugging)
   */
  getCoefficient(): number {
    return this.config.coefficient
  }
}
